import { Navigate, Route, Routes } from 'react-router-dom';
import type { Role } from '@kidzim/shared';
import { useAuth } from './lib/auth';
import { Home } from './screens/Home';
import { LegalPage } from './screens/LegalPage';
import { ParentAuth } from './screens/ParentAuth';
import { ConsentConfirm } from './screens/ConsentConfirm';
import { VerifyEmail } from './screens/VerifyEmail';
import { ResetPassword } from './screens/ResetPassword';
import { KidLogin } from './screens/KidLogin';
import { JoinRoom } from './screens/JoinRoom';
import { MagicEnter } from './screens/MagicEnter';
import { ChatRoom } from './screens/ChatRoom';
import { Buddies as KidBuddies } from './screens/Buddies';
import { ParentDashboard } from './parent/ParentDashboard';
import { Overview } from './parent/Overview';
import { Family } from './parent/Family';
import { GrownUps } from './parent/GrownUps';
import { Buddies as ParentBuddies } from './parent/Buddies';
import { Rooms } from './parent/Rooms';
import { RoomDetail } from './parent/RoomDetail';
import { Settings } from './parent/Settings';
import { Account } from './parent/Account';
import { Support } from './parent/Support';
import { AdminConsole } from './admin/AdminConsole';

function Protected({ role, children }: { role?: Role | Role[]; children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageSpinner />;
  if (!user) return <Navigate to="/" replace />;
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(user.role)) return <Navigate to="/" replace />;
  }
  return children;
}

function FullPageSpinner() {
  return (
    <div className="kz-center">
      <p className="kz-muted">Loading…</p>
    </div>
  );
}

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/parent" element={<ParentAuth />} />
      <Route path="/consent/confirm" element={<ConsentConfirm />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/kid" element={<KidLogin />} />
      <Route path="/join/:token" element={<JoinRoom />} />
      <Route path="/m/:token" element={<MagicEnter />} />
      <Route
        path="/buddies"
        element={
          <Protected role="kid">
            <KidBuddies />
          </Protected>
        }
      />
      <Route
        path="/chat/:roomId"
        element={
          <Protected role={['kid', 'parent']}>
            <ChatRoom />
          </Protected>
        }
      />

      <Route
        path="/app"
        element={
          <Protected role={['parent', 'admin']}>
            <ParentDashboard />
          </Protected>
        }
      >
        <Route index element={<Overview />} />
        <Route path="family" element={<Family />} />
        <Route path="grown-ups" element={<GrownUps />} />
        <Route path="buddies" element={<ParentBuddies />} />
        <Route path="rooms" element={<Rooms />} />
        <Route path="rooms/:id" element={<RoomDetail />} />
        <Route path="settings" element={<Settings />} />
        <Route path="account" element={<Account />} />
        <Route path="support" element={<Support />} />
      </Route>

      <Route
        path="/admin"
        element={
          <Protected role="admin">
            <AdminConsole />
          </Protected>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
